import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, filter, map, Observable, of, switchMap } from 'rxjs';
import { StartService } from '../start/start.service';
import { StartResponsePayload } from '@cpak/adp-cpak-bff-service-models';
import { Permissions } from '@cpak/adp-cpak-auth-service-models';
import { FeatureToggleService } from '../feature-toggle.service';
import { FeaturesEnum } from '@cpak/enums';

@Injectable({
  providedIn: 'root',
})
export class UserPermissionsService {
  private permissions$ = new BehaviorSubject<string[] | null>(null);
  private maxAvailableLevel = new BehaviorSubject(0);
  private hasClearFromInboxSubject = new BehaviorSubject<boolean>(false);
  private hasClearFromOutboxSubject = new BehaviorSubject<boolean>(false);
  constructor(private startService: StartService, private featureToggleService: FeatureToggleService) {
    this.startService.getStartResponse().subscribe((response: StartResponsePayload | null) => {
      if (response) {
        const { userInfo, config } = response.data;
        this.permissions$.next([
          ...new Set(
            userInfo.user.roles.flatMap(userRole => config.roles.find(role => role.key === userRole)?.permissions || [])
          ),
        ]);
        this.maxAvailableLevel.next(userInfo.user.level);
      } else {
        this.permissions$.next([]);
        this.maxAvailableLevel.next(0);
      }
    });
  }

  public getMaxAvailableLevel(): Observable<number> {
    return this.maxAvailableLevel.asObservable();
  }

  public hasPermissions(permissionsToCheck: string[]): Observable<boolean> {
    return this.permissions$.asObservable().pipe(
      filter(Boolean),
      map(permissions => permissionsToCheck.every(permission => permissions.includes(permission)))
    );
  }

  private hasPermission(permission: string): Observable<boolean> {
    return this.permissions$.asObservable().pipe(
      filter(Boolean),
      map(permissions => permissions.includes(permission))
    );
  }

  setHasClearFromInbox(status: boolean): void {
    this.hasClearFromInboxSubject.next(status);
  }
  setHasClearFromOutbox(status: boolean): void {
    this.hasClearFromOutboxSubject.next(status);
  }

  getHasClearFromInbox(): Observable<boolean> {
    return this.hasClearFromInboxSubject.asObservable();
  }
  getHasClearFromOutbox(): Observable<boolean> {
    return this.hasClearFromOutboxSubject.asObservable();
  }
  public hasPackWritePermission(): Observable<boolean> {
    return this.hasPermission(Permissions.packWrite);
  }

  public hasPackDeletePermission(): Observable<boolean> {
    return this.hasPermission(Permissions.packDelete);
  }

  public hasOwnershipTakePermission(): Observable<boolean> {
    return this.hasPermission(Permissions.ownershipTake);
  }

  public hasMakeContractPermission(): Observable<boolean> {
    return this.hasPermission(Permissions.contractMake);
  }

  public hasMakeSisterContractPermission(): Observable<boolean> {
    return this.hasPermission(Permissions.sisterContractMake);
  }

  public hasUserEditPermission(): Observable<boolean> {
    return this.hasPermission(Permissions.userEdit);
  }

  public hasQuoteTemplateManagePermission(): Observable<boolean> {
    return this.hasPermission(Permissions.quoteTemplateManage);
  }

  public hasMasterTemplateManagePermission(): Observable<boolean> {
    return this.hasPermission(Permissions.masterTemplateManage);
  }

  public hasDocumentTemplateManagePermission(): Observable<boolean> {
    return this.featureToggleService
      .isFeatureEnabled(FeaturesEnum.ManageDocumentTemplate)
      .pipe(switchMap(isEnabled => (isEnabled ? this.hasPermission(Permissions.documentTemplateManage) : of(false))));
  }

  public hasEmailTemplateManagePermission(): Observable<boolean> {
    return this.featureToggleService
      .isFeatureEnabled(FeaturesEnum.ManageEmailTemplate)
      .pipe(
        switchMap(isEnabled => (isEnabled ? this.hasPermission(Permissions.notificationTemplateManage) : of(false)))
      );
  }

  public hasTemplateManagePermission(): Observable<boolean> {
    return combineLatest([this.hasQuoteTemplateManagePermission(), this.hasMasterTemplateManagePermission()]).pipe(
      map(
        ([hasQuoteTemplateManagePermission, hasMasterTemplateManagePermission]) =>
          hasQuoteTemplateManagePermission || hasMasterTemplateManagePermission
      )
    );
  }

  public hasMassReplaceManagePermission(): Observable<boolean> {
    return this.featureToggleService
      .isFeatureEnabled(FeaturesEnum.MassReplace)
      .pipe(switchMap(isEnabled => (isEnabled ? this.hasPermission(Permissions.massReplaceManage) : of(false))));
  }

  public hasStagePackPermission(): Observable<boolean> {
    return this.hasPermission(Permissions.packStage);
  }

  public hasSomeMaintenancePermission(): Observable<boolean> {
    return combineLatest([
      this.hasUserEditPermission(),
      this.hasTemplateManagePermission(),
      this.hasStagePackPermission(),
      this.hasMassReplaceManagePermission(),
    ]).pipe(
      map(permissions => {
        return permissions.some(Boolean);
      })
    );
  }

  public hasCountryAffiliateInboxOutboxViewPermission(): Observable<boolean> {
    return this.hasPermission(Permissions.countryAffiliateInboxOutboxView);
  }

  public hasContractAdminInboxOutboxViewPermission(): Observable<boolean> {
    return this.hasPermission(Permissions.contractAdminInboxOutboxView);
  }

  public hasPricingViewCountryMSPPermission(): Observable<boolean> {
    return this.hasPermission(Permissions.pricingViewCountryMSP);
  }

  public hasPricingViewLandedMSPPermission(): Observable<boolean> {
    return this.hasPermission(Permissions.pricingViewLandedMSP);
  }

  public hasPricingManageCountryGuidelines(): Observable<boolean> {
    return this.hasPermission(Permissions.countryGuidelinesManage);
  }

  public hasPricingManagePartCost(): Observable<boolean> {
    return this.hasPermission(Permissions.partCostManage);
  }

  public hasPermissionToViewAdminPricingTab(): Observable<boolean> {
    return combineLatest([this.hasPricingManageCountryGuidelines(), this.hasPricingManagePartCost()]).pipe(
      map(([hasPricingManageCountryGuidelines, hasPricingManagePartCost]) => {
        return hasPricingManageCountryGuidelines || hasPricingManagePartCost;
      })
    );
  }
  public hasPricingViewCostPermission(): Observable<boolean> {
    return this.hasPermission(Permissions.pricingViewCost);
  }

  public hasRoutingSendToSFPermission(): Observable<boolean> {
    return this.hasPermission(Permissions.routingSendToSF);
  }

  public hasRoutingSendToCPQPermission(): Observable<boolean> {
    return this.hasPermission(Permissions.routingSendToCPQ);
  }

  public hasDocumentTemplateGenerateAdminOnlyPermission(): Observable<boolean> {
    return this.hasPermission(Permissions.documentTemplateGenerateAdminOnly);
  }

  public hasPackArchivePermission(): Observable<boolean> {
    return this.hasPermission(Permissions.packArchive);
  }

  public hasAccountEditPermission(): Observable<boolean> {
    return this.hasPermission(Permissions.accountEdit);
  }

  public hasSharedPackMakePermission(): Observable<boolean> {
    return this.hasPermission(Permissions.sharedPacksMake);
  }

  public hasBulkPricingChangePermission(): Observable<boolean> {
    return this.hasPermission(Permissions.pricingBulkPriceChange);
  }

  public hasCountryGuidelinesReadonlyPermission(): Observable<boolean> {
    return this.hasPermission(Permissions.countryGuidelinesReadonly);
  }
}
